import {
  ApplicationCreditFlowNodeDetail,
  ApplicationIndividual,
  GetApplicationPartiesPayload,
  IndividualRoleType,
  NewWebService,
  SignerRoleTypes,
  apiSignerRoleFromRoleFlags,
  commercialEntityRoleType,
  individualRoleType,
} from '@portal-workspace/grow-shared-library';
import moment from 'moment';

/**
 * How the credit tab names the party a row belongs to, resolved against /get-application-parties.
 *
 * Kept out of the components so it can be tested without constructing them: both reach
 * DatepickerModule through their imports, which has a circular import that throws while Angular
 * builds the module scope.
 *
 * `parties` is null until the fetch resolves, which is why every one of these tolerates it rather
 * than assuming a loaded payload.
 */

/**
 * The legacy `Role ?? SignerRole` pair this column displayed. Role was null only for someone holding
 * no role and no guarantee, which is exactly when the legacy expression fell through to SignerRole.
 */
const applicantTypeOf = (individual: ApplicationIndividual): IndividualRoleType | SignerRoleTypes =>
  individualRoleType(individual)
  ?? apiSignerRoleFromRoleFlags({ isSigner: individual.roles.some(role => role.isSigner) }, individual.isGuarantor);

export const creditAlertApplicant = (
  parties: GetApplicationPartiesPayload | null,
  detail: Pick<ApplicationCreditFlowNodeDetail, 'individualId' | 'abn' | 'acn'> | null | undefined,
): string => {
  if (detail?.individualId) {
    // individualId carries the GUID, which the new shape publishes as guid; its id is the numeric key.
    const individual = parties?.individuals.find(individual => individual.guid === detail.individualId);
    return individual ? `${individual?.givenName ?? ''} ${individual?.surname ?? ''}` : ``;
  }
  if (detail?.abn || detail?.acn) {
    const entity = parties?.commercialEntities.find(entity =>
      (entity?.abn ?? null) == (detail.abn ?? null) &&
      (entity?.acn ?? null) == (detail.acn ?? null)
    );
    return entity ? `${entity?.legalName ?? ''}` : ``;
  }
  return ``;
};

export const creditWebServiceApplicant = (
  parties: GetApplicationPartiesPayload | null,
  webService: Pick<NewWebService, 'level' | 'individualFirstName' | 'individualLastName' | 'individualDoB' | 'ABN' | 'ACN'>,
): { applicant: string, applicantType: string } => {
  if (webService.level === 'individual') {
    const individual = (parties?.individuals ?? []).find(individual =>
      individual.givenName?.toUpperCase() === webService.individualFirstName?.toUpperCase() &&
      individual.surname?.toUpperCase() === webService.individualLastName?.toUpperCase() &&
      Date.parse(individual.dateOfBirth ?? '') === Date.parse(webService.individualDoB ?? '')
    );
    return individual
      ? {
        applicant: `${individual.givenName} ${individual.surname} (${moment(individual.dateOfBirth).format('DD/MM/YYYY')})`,
        applicantType: `${applicantTypeOf(individual)}`,
      }
      : { applicant: '', applicantType: '' };
  }

  const entity = (parties?.commercialEntities ?? []).find(entity =>
    ((webService.ABN && webService.ABN !== "") ? webService.ABN : null) ===
    ((entity.abn && entity.abn !== "") ? entity.abn : null) &&
    ((webService.ACN && webService.ACN !== "") ? webService.ACN : null) ===
    ((entity.acn && entity.acn !== "") ? entity.acn : null)
  );
  return entity
    ? { applicant: `${entity.legalName}`, applicantType: `${commercialEntityRoleType(entity)}` }
    : { applicant: '', applicantType: '' };
};
